// Share INTO the app (docs/PARITY.md P134) — the Web Share Target half of the apps' share sheet
// (iOS ShareExtension, Android ACTION_SEND): the installed PWA appears in the system share sheet,
// sw.js receives the POST (manifest `share_target`), keeps what was shared in Cache Storage and opens
// `/?share=<id>`; the page asks "Compartir en…", then stages the files in that conversation's
// composer and puts the text in its draft. Nothing is sent without the reader pressing Enviar.
import { tf } from '../i18n/i18n';

/** What sw.js stores beside the files (`/__share/<id>/meta`). */
export interface SharedPayloadMeta {
  title: string;
  text: string;
  url: string;
  files: Array<{ name: string; type: string; size: number }>;
  at: number;
}

export const SHARE_CACHE = 'satori-share-v1';
export const SHARE_PARAM = 'share';
/** A share older than this is stale (the reader closed the picker long ago): dropped, never offered. */
export const SHARE_MAX_AGE_MS = 24 * 3600_000;

/** One text out of the three fields a share sheet fills: no repeated URL, no title that IS the text. */
export function composedText(meta: Pick<SharedPayloadMeta, 'title' | 'text' | 'url'>): string {
  const title = meta.title.trim();
  const text = meta.text.trim();
  const url = meta.url.trim();
  const parts: string[] = [];
  if (title && title !== text && !(url && title === url)) parts.push(title);
  if (text) parts.push(text);
  if (url && !text.includes(url)) parts.push(url);
  return parts.join('\n');
}

/** The picker's one-line summary: the first file name, «y N más» for the rest; else the text. */
export function shareSummary(meta: SharedPayloadMeta): string {
  if (meta.files.length) {
    const first = meta.files[0].name || '…';
    return meta.files.length > 1 ? `${first} ${tf('share.moreFiles', meta.files.length - 1)}` : first;
  }
  const text = composedText(meta);
  return text.length > 120 ? text.slice(0, 119) + '…' : text;
}

/** Appends shared text to whatever draft the conversation already holds (never overwrites it). */
export function mergeIntoDraft(draft: string, shared: string): string {
  if (!shared) return draft;
  if (!draft.trim()) return shared;
  return draft.replace(/\s+$/, '') + '\n' + shared;
}

export function isFreshShare(meta: Pick<SharedPayloadMeta, 'at'>, now: number): boolean {
  return Number.isFinite(meta.at) && now - meta.at >= 0 && now - meta.at < SHARE_MAX_AGE_MS;
}
